import PatternHelper from './PatternHelper';
import PieceHelper from './PieceHelper';
import ScaleHelper from './ScaleHelper';
import { SOLITUDE, WHITE } from '../constants/colors';

const THUMBNAIL_WIDTH = 500;
const BORDER = { x: 10, y: 10 };

const round = (value) => Math.round(value * 100) / 100;

/**
 * Draws pattern thumbnails as SVG. This replaces the PNGs the API used to draw with node-canvas,
 * so thumbnails can be made in the browser and stored in the pattern document.
 * The drawing matches the old PNGs: pieces scaled to 500px wide, mirrored pieces included,
 * closed pieces filled and open pieces stroked, on a white background.
 */
export default class ThumbnailHelper {
  static createThumbnailSvg(pattern) {
    const pieces = (pattern && pattern.pieces) || {};
    const realBounds = PatternHelper.getBounds({ pieces, texts: {} }, false, BORDER);
    const scale = realBounds.w > 0 ? THUMBNAIL_WIDTH / realBounds.w : 1;

    const thumbnailPattern = PatternHelper.createPattern();
    Object.values(pieces).forEach((piece) => {
      thumbnailPattern.pieces[piece.id] = ScaleHelper.pieceScale(piece, scale);
      const reflectedPiece = piece.mirror ? PieceHelper.getReflectedPiece(thumbnailPattern.pieces[piece.id]) : null;
      if (reflectedPiece) {
        thumbnailPattern.pieces[reflectedPiece.id] = reflectedPiece;
      }
    });

    const bounds = PatternHelper.getBounds(thumbnailPattern, false, BORDER);
    if (bounds.w === 0 || bounds.h === 0) {
      bounds.w = THUMBNAIL_WIDTH;
      bounds.h = THUMBNAIL_WIDTH;
    }

    const paths = Object.values(thumbnailPattern.pieces)
      .map((piece) => ThumbnailHelper.piecePath(piece, bounds))
      .filter(Boolean);

    const width = round(bounds.w);
    const height = round(bounds.h);
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`
      + `<rect width="100%" height="100%" fill="${WHITE}"/>${paths.join('')}</svg>`;
  }

  static piecePath(piece, bounds) {
    const knots = Object.values(PieceHelper.interpolateKnots(piece).knots);
    if (!knots.length) return null;

    const lastIndex = piece.knots.length - 1;
    const x = (value) => round(value - bounds.x);
    const y = (value) => round(value - bounds.y);
    const commands = [`M${x(knots[0].x)} ${y(knots[0].y)}`];

    knots.forEach((knot, idx) => {
      let next;
      if (idx === lastIndex && piece.isClosed) {
        next = knots[0];
      } else if (idx === lastIndex && !piece.isClosed) {
        return;
      } else {
        next = knots[idx + 1];
      }
      if (!next) return;
      commands.push(`C${x(knot.x + knot.rH.x)} ${y(knot.y + knot.rH.y)} ${x(next.x + next.lH.x)} ${y(next.y + next.lH.y)} ${x(next.x)} ${y(next.y)}`);
    });

    // Open pieces are arrows and are stroked, closed pieces are filled.
    if (piece.isClosed) {
      return `<path d="${commands.join('')}Z" fill="${SOLITUDE}"/>`;
    }
    return `<path d="${commands.join('')}" fill="none" stroke="${SOLITUDE}"/>`;
  }

  /** A data URL that can be stored in the pattern document and used directly as an image source. */
  static createThumbnailDataUrl(pattern) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(ThumbnailHelper.createThumbnailSvg(pattern))}`;
  }

  /** Thumbnails that are not data URLs point at the old Google Cloud Storage buckets and should be redrawn. */
  static isStoredThumbnail(thumbnail) {
    return typeof thumbnail === 'string' && thumbnail.startsWith('data:image/svg+xml');
  }
}
